<template>
  <AdminCompanySectionComponent
    title="Referral Settings"
    testId="referral-settings"
    emptyText="Select a company to configure referrals."
    errorText="Referral settings could not be loaded — saving is disabled so the existing referrals stay unchanged."
    :company="selectedCompany"
    :form="referralSettings"
    :saveDisabled="overCap"
  >
    <div class="row">
      <div class="col-lg-6" style="max-width: 520px">
        <div class="mb-3">
          <label class="form-label">Referrals (max 2)</label>
          <CompanySelector
            v-model="referralIds"
            :max="2"
            size="sm"
            data-testid="referral-settings-referrers"
          />
          <div
            v-for="referral in referrals"
            :key="referral.id"
            class="d-flex align-items-center gap-2 mt-2"
          >
            <span class="small" style="min-width: 220px">{{
              referral.name ?? `#${referral.id}`
            }}</span>
            <input
              v-model.number="referral.overridePct"
              type="number"
              step="0.01"
              min="0"
              max="100"
              class="form-control form-control-sm"
              style="max-width: 140px"
              :placeholder="`config rate (max ${referral.configMaxRatePct}%)`"
              :data-testid="`referral-rate-${referral.id}`"
              :disabled="!isAdmin"
            />
            <span class="small text-secondary">%</span>
          </div>
          <div class="form-text">
            Combined referral rate: {{ totalRatePct.toFixed(2) }}% (cap
            {{ capPct }}%)
          </div>
          <div
            v-if="overCap"
            class="text-danger small"
            data-testid="referral-cap-error"
          >
            Combined referral rate exceeds the {{ capPct }}% cap — lower the
            overrides or remove a referrer.
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label" for="referralRateCap"
            >Referral rate cap (%)</label
          >
          <input
            id="referralRateCap"
            v-model.number="capOverridePct"
            type="number"
            step="0.01"
            min="0.01"
            max="100"
            class="form-control form-control-sm referral-rate-cap-input"
            placeholder="default 30"
            data-testid="referral-rate-cap"
            :disabled="!isAdmin"
          />
          <div class="form-text">
            Leave empty to use the default 30% cap on the combined referral
            rate.
          </div>
        </div>
      </div>
    </div>
  </AdminCompanySectionComponent>
</template>

<script setup lang="ts">
import CompanySelector from "@/components/common/CompanySelector.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { ADMIN_IDS } from "@/constants";
import AdminCompanySectionComponent from "@/dashboard/admin/AdminCompanySectionComponent.vue";
import { useCompanySectionForm } from "@/dashboard/admin/useCompanySectionForm";
import type { Account, Company } from "@/types";
import { computed, ref } from "vue";

interface ReferralSetting {
  referrer_company_id: number;
  rate_override: string | null;
  name: string | null;
  config_max_rate: string | null;
}

/** GET and PUT /api/admin/company/{id}/referral-settings both answer with this shape. */
interface ReferralSettings {
  referrals: ReferralSetting[];
  referral_rate_cap: string | null;
}

interface ReferralRow {
  id: number;
  name: string | null;
  overridePct: number | null; // percent in the UI, fraction on the wire
  configMaxRatePct: number;
  /** Whether name/configMaxRatePct are the real values rather than the conservative placeholder. */
  ratesResolved: boolean;
}

const props = defineProps<{
  selectedCompany: Company | null;
  accountData: Account;
}>();

const DEFAULT_CAP_PCT = 30;

// Only full-access admins may edit the rates and the cap; everyone else may still change who
// refers the company. The backend enforces the same rule and rejects an actual change.
const isAdmin = computed(
  () =>
    props.accountData.admin_account_id != null &&
    ADMIN_IDS.FULL_ACCESS.includes(props.accountData.admin_account_id)
);

// Selection and row data are kept apart so deselecting a referrer doesn't destroy its row: the row
// survives in rowsById and a reselect restores it, rather than sending a null override the backend
// reads as clearing one. Replaced wholesale on load, so no other company's rows can leak in.
const selectedIds = ref<number[]>([]);
const rowsById = ref<Record<number, ReferralRow>>({});
// Per-company cap on the combined referral rate, as a percent in the UI (fraction on the wire).
// null = the default 30% cap.
const capOverridePct = ref<number | null>(null);
// Ids with a max-rate fetch already in flight, so re-running the referralIds setter (e.g. adding a
// second referrer while the first's fetch is pending) doesn't fire a duplicate concurrent request.
const pendingMaxRate = new Set<number>();

const referrals = computed(() =>
  selectedIds.value.map((id) => rowsById.value[id])
);

// Rows are created in the setter rather than a watcher so `referrals` never renders a hole between
// the selection changing and the row appearing.
const referralIds = computed<number[]>({
  get: () => selectedIds.value,
  set: (ids) => {
    selectedIds.value = ids;
    for (const id of ids) {
      rowsById.value[id] ??= {
        id,
        name: null,
        overridePct: null,
        configMaxRatePct: DEFAULT_CAP_PCT,
        ratesResolved: false,
      };
      if (!rowsById.value[id].ratesResolved) fetchMaxRate(id);
    }
  },
});

const totalRatePct = computed(() =>
  referrals.value.reduce(
    (sum, r) =>
      sum +
      (typeof r.overridePct === "number" && !Number.isNaN(r.overridePct)
        ? r.overridePct
        : r.configMaxRatePct),
    0
  )
);
const capPct = computed(() => {
  const override = capOverridePct.value;
  return typeof override === "number" && !Number.isNaN(override)
    ? override
    : DEFAULT_CAP_PCT;
});
const overCap = computed(() => totalRatePct.value > capPct.value);

const referralSettings = useCompanySectionForm<ReferralSettings>({
  company: () => props.selectedCompany,
  resource: "referral-settings",
  apply: (data) => {
    capOverridePct.value = toRoundedPct(data.referral_rate_cap);
    selectedIds.value = (data.referrals ?? []).map(
      (r) => r.referrer_company_id
    );
    rowsById.value = Object.fromEntries(
      (data.referrals ?? []).map((r) => [
        r.referrer_company_id,
        {
          id: r.referrer_company_id,
          name: r.name,
          overridePct: toRoundedPct(r.rate_override),
          configMaxRatePct: toRoundedPct(r.config_max_rate) ?? DEFAULT_CAP_PCT,
          ratesResolved: true,
        },
      ])
    );
  },
  // The cap and the overrides are sent as loaded even when this admin may not edit them: the
  // backend rejects a *change*, not their presence, so echoing keeps the payload complete.
  payload: () => ({
    referrals: referrals.value.map((r) => ({
      referrer_company_id: r.id,
      rate_override: toFractionOrNull(r.overridePct),
    })),
    referral_rate_cap: toFractionOrNull(capOverridePct.value),
  }),
  savedMessage: ["Saved", "Referral settings updated."],
});

/**
 * Resolves a referrer's name and max config tier rate from
 * GET /api/admin/commission/max-rate/<id> so the client-side cap total is accurate before saving.
 * On failure the row stays unresolved, so a later selection change retries; it keeps the
 * conservative default meanwhile and the backend cap check remains authoritative.
 */
async function fetchMaxRate(id: number) {
  if (pendingMaxRate.has(id)) return;
  pendingMaxRate.add(id);
  try {
    const res = await goSolidApi.get(`/api/admin/commission/max-rate/${id}`);
    // A missing rate would make configMaxRatePct NaN, which poisons the combined total and makes
    // overCap false — leaving Save enabled on an unusable form.
    Object.assign(rowsById.value[id] ?? {}, {
      name: res.data.name ?? null,
      configMaxRatePct:
        toRoundedPct(res.data.config_max_rate) ?? DEFAULT_CAP_PCT,
      ratesResolved: true,
    });
  } catch {
    // Leave the row on its default and allow a retry.
  } finally {
    pendingMaxRate.delete(id);
  }
}

/** A wire fraction as a UI percent, rounded to two decimals. */
function toRoundedPct(fraction: string | null | undefined): number | null {
  return fraction != null
    ? Math.round(parseFloat(fraction) * 100 * 100) / 100
    : null;
}

function toFractionOrNull(pct: number | null): number | null {
  return typeof pct === "number" && !Number.isNaN(pct) ? pct / 100 : null;
}
</script>

<style scoped>
.referral-rate-cap-input {
  max-width: 140px;
}
</style>
